import { PROJECTION_ASSUMPTION_COPY, type ProjectedFigures } from '@wend/shared';
import { Kicker, ProportionBar, StateChip } from '@wend/ui';

import { formatCount } from '../lib/format';

/**
 * Projected delivery, opens and clicks — drawn under the measured figures, never
 * among them.
 *
 * ## IT LIVES IN `components/`, AND THAT IS ARCHITECTURAL
 *
 * Slice 27 put it in `routes/overview/` because the Overview was its only
 * caller. Slice 28 gave it a second one on the campaign page, and
 * `verify-page-hardening` refused the import: it holds a CONTROL saying no
 * screen's import closure may reach `routes/overview/`, so that a change to a
 * campaign cannot silently pull in a dashboard card. The rule is right and the
 * file was in the wrong place — a component two route trees draw belongs
 * beside `CampaignReach` and `SendGuardNotice`, which are there for the same
 * reason.
 *
 * ## WHY IT IS A SEPARATE COMPONENT RATHER THAN A PROP
 *
 * **Its only prop is a {@link ProjectedFigures}.** There is no `value: number`
 * and no `label`/`count` pair, so a measured count cannot be passed to it and
 * this cannot be reached with anything but the contract's own shape. That is the
 * same technique `NoFigure` uses — no prop to pass a `0` to — and the same one
 * `ProjectedCountsPanel` uses beside `SegmentCountsPanel`, which is the
 * precedent in this codebase for a projection getting its own component instead
 * of a variant flag on the measured one.
 *
 * A `projected` boolean on {@link CountTile} would have been three lines
 * shorter and would have meant that every tile on the Overview was one argument
 * away from claiming an assumption as a count.
 *
 * ## WHY IT LOOKS THE WAY IT DOES
 *
 * Three signals, none of them colour alone:
 *
 *   - **A rank below.** 1.5rem against the measured row's 2rem. An open rate has
 *     drawn at the lower rank since slice 11 for the same reason: a figure that
 *     is not a measurement must not sit at the same visual weight as one that
 *     is.
 *   - **A dashed edge.** Every measured card on this screen has a solid one. It
 *     reads as provisional without needing a tint, and it survives a reader with
 *     no colour vision and a greyscale screenshot.
 *   - **The assumptions, visible.** Not a tooltip — `verify-page-hardening` bans
 *     `title=` on a screen file, and a skimmer never opens one. Not folded
 *     either: a projection whose assumptions are one click away is an exact
 *     figure as far as a skimmer is concerned, which is the whole failure this
 *     is trying not to repeat.
 *
 * There is no threshold bar, no band and no verdict here, and there must never
 * be one. A band is a claim that a number is good or bad; nothing assumed can
 * earn one.
 *
 * ## WHERE IT SITS, AND WHY THAT ORDER
 *
 * Under the real counts **and under the strip that says nothing is measured**,
 * so a reader meets the absence before they meet the projection. Reversing those
 * two would let the projection read as the answer and the strip as a footnote.
 */
export function ProjectedFigureRow({ projection }: { projection: ProjectedFigures }) {
  return (
    <div
      data-role="projected-figures"
      data-projection-basis={projection.basis}
      className="flex flex-col gap-3.5 rounded-base border border-dashed border-line px-4 py-3.5"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
        <StateChip state="projected" />
        <p className="text-[0.82rem] leading-snug text-muted">
          Over the {formatCount(projection.unreported)} messages nothing has been reported for.
        </p>
      </div>

      {/*
        THE TWO RINGS, AND WHY DELIVERED IS NOT ONE OF THEM.

        A ring answers *how much of the whole is this?* Under the delivery
        assumption, delivered IS the whole — every unreported message is assumed
        to have arrived — so a ring for it would be a full circle showing 100%,
        which is not a fact about this workspace but a restatement of the
        assumption. It stays a figure. Opens and clicks are shares of it and read
        far better as shares than as `335` beside `1,116`.

        **The tone is `info`, and it is hard-coded here rather than passed in.**
        `good` would put a verdict on an assumed number, which is the shape of
        both criticals this project has shipped.

        **And the centre carries the COUNT, not the share as a percentage.** The
        first draft printed `30%` there, and `verify-dashboard` refused it: the
        rule in this state is that no percentage appears anywhere, because a
        percentage is what a skimmer reads as a measured rate. The ring already
        shows the proportion — that is the entire job of a ring — so the number in
        the middle can be the count and the denominator can sit under the label.
        Nothing is lost and the rule is untouched. Verdict colours belong on
        `ThresholdBar`, where a measured rate is drawn against the AWS line it is
        being judged by. `verify-dashboard` asserts no verdict tone reaches this
        component.
      */}
      <div className="flex flex-wrap items-start gap-x-10 gap-y-5">
        <dl className="flex flex-wrap gap-x-10 gap-y-3">
          <ProjectedFigure label="Delivered" value={projection.delivered} />
        </dl>

        <div className="flex flex-wrap gap-x-8 gap-y-4">
          <ProjectedShare
            label="Opened"
            value={projection.opened}
            total={projection.delivered}
          />
          <ProjectedShare
            label="Clicked"
            value={projection.clicked}
            total={projection.delivered}
          />
        </div>
      </div>

      {/*
        One line per assumption, from the contract's own record. Never empty:
        `projectedFiguresSchema.assumptions` is `.min(1)`, so a projection that
        reached this component with nothing to qualify it would have failed to
        parse at the API's response boundary.
      */}
      <ul className="flex flex-col gap-1 border-t border-line pt-3">
        {projection.assumptions.map((assumption) => (
          <li key={assumption} className="max-w-prose text-[0.76rem] leading-snug text-muted">
            {PROJECTION_ASSUMPTION_COPY[assumption]}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One projected figure. Local to this file on purpose: exporting it would make
 * the rank and the dashed context optional, and a projected figure drawn on its
 * own somewhere else is a number with no chip near it.
 */
function ProjectedFigure({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1">
      <Kicker as="dt">{label}</Kicker>
      {/* The measured tiles' figure role (slice 66), at their `md` size: sans,
          tight, tabular. Written out rather than imported from OverviewFigures,
          for the closure rule this file's header gives. */}
      <dd className="text-[1.5rem] font-semibold leading-[1.05] tracking-[-0.04em] tabular-nums text-text">
        {formatCount(value)}
      </dd>
    </div>
  );
}

/**
 * One projected share: a label, the count, and a bar under it.
 *
 * BARS RATHER THAN RINGS, AND WHERE THE LAYOUT LIVES
 * -----------------------------------------------------------------------------
 * The workspace owner asked for small horizontal bars in place of the rings
 * this row used to draw, which is also what the approved design has. The kit's
 * `ProportionBar` is a bare track by design — its header calls it "the
 * breakdown strip underneath a chart" — so the label and the figure are
 * composed here rather than pushed into the primitive. That is the same split
 * the ring already had: `formatCount` lives in the web app, and a design-system
 * primitive that knew about thousands separators would have to know about
 * locales next.
 *
 * `tone="info"`, never a verdict tone. Every number this component draws is a
 * projection, and `good` on an assumed figure is the exact shape of the two
 * criticals this project has shipped. `verify-dashboard` asserts it.
 *
 * The bar is `aria-hidden` inside the primitive, and both numbers are real text
 * here, so a screen reader gets the figures rather than a shrug.
 */
function ProjectedShare({
  label,
  value,
  total,
}: {
  label: string;
  value: number;
  total: number;
}) {
  return (
    <div data-role="projected-bar" className="flex min-w-[9.5rem] flex-1 flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[0.8rem] text-muted">{label}</span>
        <span className="text-[0.95rem] font-medium tabular-nums text-text">
          {formatCount(value)}
        </span>
      </div>
      <ProportionBar tone="info" value={value} total={total} />
      <span className="text-[0.72rem] tabular-nums text-muted">of {formatCount(total)}</span>
    </div>
  );
}
